import {
  COCKTAIL_STATUSES,
  INGREDIENT_CATEGORIES,
  TECHNIQUES,
  UNITS,
  type CocktailInput,
  type IngredientInput,
  type Settings,
  type TastingInput,
  type VersionInput,
} from './types.js';

export type FieldErrors = Record<string, string>;
export interface Validated<T> {
  value?: T;
  errors: FieldErrors;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

export function normalizeTags(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  for (const t of raw) {
    const tag = str(t).toLowerCase();
    if (tag) seen.add(tag);
  }
  return [...seen].sort();
}

export function validateCocktailInput(raw: unknown): Validated<CocktailInput> {
  const errors: FieldErrors = {};
  const o = isObj(raw) ? raw : {};
  const name = str(o.name);
  if (!name) errors.name = 'Give the drink a name.';
  else if (name.length > 120) errors.name = 'Keep the name under 120 characters.';
  const status = o.status ?? 'idea';
  if (!COCKTAIL_STATUSES.includes(status as never)) errors.status = 'Pick a valid status.';
  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    value: {
      name,
      status: status as CocktailInput['status'],
      tags: normalizeTags(o.tags),
      parentId: typeof o.parentId === 'string' ? o.parentId : null,
    },
  };
}

export function validateVersionInput(
  raw: unknown,
  opts: { requireChangeNote: boolean },
): Validated<VersionInput> {
  const errors: FieldErrors = {};
  const o = isObj(raw) ? raw : {};
  if (!TECHNIQUES.includes(o.technique as never)) errors.technique = 'Pick a technique.';
  const changeNote = str(o.changeNote);
  if (opts.requireChangeNote && !changeNote) {
    errors.changeNote = 'Say what changed and why.';
  }
  const linesRaw = Array.isArray(o.lines) ? o.lines : [];
  if (linesRaw.length === 0) errors.lines = 'Add at least one ingredient.';
  const lines: VersionInput['lines'] = [];
  linesRaw.forEach((l, i) => {
    const line = isObj(l) ? l : {};
    const ingredientId = str(line.ingredientId);
    const amount = Number(line.amount);
    if (!ingredientId) errors[`lines.${i}.ingredientId`] = 'Choose an ingredient.';
    if (!Number.isFinite(amount) || amount <= 0)
      errors[`lines.${i}.amount`] = 'Enter an amount above 0.';
    if (!UNITS.includes(line.unit as never)) errors[`lines.${i}.unit`] = 'Pick a unit.';
    lines.push({ ingredientId, amount, unit: line.unit as VersionInput['lines'][number]['unit'] });
  });
  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    value: {
      technique: o.technique as VersionInput['technique'],
      glass: str(o.glass),
      ice: str(o.ice),
      garnish: str(o.garnish),
      methodNotes: typeof o.methodNotes === 'string' ? o.methodNotes.trim() : '',
      changeNote,
      lines,
    },
  };
}

export function validateIngredientInput(raw: unknown): Validated<IngredientInput> {
  const errors: FieldErrors = {};
  const o = isObj(raw) ? raw : {};
  const name = str(o.name);
  if (!name) errors.name = 'Give the ingredient a name.';
  const category = o.category ?? 'other';
  if (!INGREDIENT_CATEGORIES.includes(category as never)) errors.category = 'Pick a category.';
  const abvPct = o.abvPct === undefined || o.abvPct === '' ? 0 : Number(o.abvPct);
  if (!Number.isFinite(abvPct) || abvPct < 0 || abvPct > 100)
    errors.abvPct = 'ABV must be between 0 and 100.';
  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    value: {
      name,
      category: category as IngredientInput['category'],
      abvPct,
      notes: typeof o.notes === 'string' ? o.notes.trim() : '',
    },
  };
}

export function validateTastingInput(raw: unknown): Validated<TastingInput> {
  const errors: FieldErrors = {};
  const o = isObj(raw) ? raw : {};
  const versionId = str(o.versionId);
  if (!versionId) errors.versionId = 'Pick the version you tasted.';
  const score = (key: string, label: string): number => {
    const n = Number(o[key]);
    if (!Number.isInteger(n) || n < 1 || n > 5) errors[key] = `${label} must be 1 to 5.`;
    return n;
  };
  const rating = score('rating', 'Rating');
  const sweet = score('sweet', 'Sweet');
  const sour = score('sour', 'Sour');
  const bitter = score('bitter', 'Bitter');
  const strong = score('strong', 'Strong');
  const parsed = typeof o.date === 'string' ? new Date(o.date) : new Date();
  if (Number.isNaN(parsed.getTime())) errors.date = 'Enter a valid date.';
  if (Object.keys(errors).length) return { errors };
  return {
    errors,
    value: {
      versionId,
      date: parsed.toISOString(),
      rating,
      sweet,
      sour,
      bitter,
      strong,
      notes: typeof o.notes === 'string' ? o.notes.trim() : '',
    },
  };
}

/** Validates a partial settings update. Unknown keys are errors. */
export function validateSettingsPatch(raw: unknown): Validated<Partial<Settings>> {
  const errors: FieldErrors = {};
  const o = isObj(raw) ? raw : {};
  const out: Partial<Settings> = {};
  for (const [k, v] of Object.entries(o)) {
    switch (k) {
      case 'defaultUnit':
        if (v === 'oz' || v === 'ml') out.defaultUnit = v;
        else errors[k] = 'Choose oz or ml.';
        break;
      case 'theme':
        if (v === 'system' || v === 'light' || v === 'dark') out.theme = v;
        else errors[k] = 'Choose system, light or dark.';
        break;
      case 'brandColor':
        if (typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v)) out.brandColor = v;
        else errors[k] = 'Use a hex colour like #0E2347.';
        break;
      case 'dilutionShake':
      case 'dilutionStir':
      case 'dilutionBuild':
      case 'dilutionBlend':
        if (typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1) out[k] = v;
        else errors[k] = 'Dilution must be between 0% and 100%.';
        break;
      default:
        errors[k] = 'Unknown setting.';
    }
  }
  return Object.keys(errors).length ? { errors } : { errors, value: out };
}
